<template>
    <div class="weeduc-admin-page weeduc-doubt-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="loading ? $t('admin.doubts.titleFallback') : doubt.title || $t('admin.doubts.titleFallback')"
                    :subtitle="headerSubtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <span
                    v-if="!loading"
                    class="weeduc-doubt-status"
                    :class="{
                        'is-pending': doubt.status === 0,
                        'is-answered': doubt.status === 1,
                        'is-deleted': doubt.status === 3,
                    }"
                >
                    {{ getStatusText(doubt.status) }}
                </span>
                <span v-if="!loading && doubt.dateInsert" class="weeduc-doubt-date">
                    {{ $t('admin.doubts.createdAt') }} {{ formatDate(doubt.dateInsert) }}
                </span>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div v-if="loading" class="weeduc-admin-page__panel weeduc-doubt-panel">
                <div class="weeduc-doubt-loading">
                    <Loader :isActive="false" />
                </div>
            </div>

            <div v-else class="weeduc-admin-page__panel weeduc-doubt-panel">
                <div class="weeduc-doubt-chat">
                    <div class="weeduc-doubt-messages" ref="chatMessages">
                        <template v-if="messages.length">
                            <div
                                v-for="(message, index) in messages"
                                :key="message.id || index"
                                class="weeduc-doubt-message"
                                :class="index % 2 === 0 ? 'is-student' : 'is-teacher'"
                            >
                                <div class="weeduc-doubt-message__header">
                                    <div class="weeduc-doubt-message__user">
                                        <img
                                            :src="message.userFinalPhoto || '/default-avatar.png'"
                                            class="weeduc-doubt-message__avatar"
                                            alt=""
                                        />
                                        <span class="weeduc-doubt-message__name">
                                            {{ message.userName }}
                                        </span>
                                    </div>
                                    <span class="weeduc-doubt-message__time">
                                        {{ formatDate(message.dateInsert) }}
                                    </span>
                                </div>
                                <div
                                    class="weeduc-doubt-message__body"
                                    v-html="message.message"
                                />
                            </div>
                        </template>
                        <div v-else class="weeduc-doubt-empty">
                            <fa icon="far fa-comments" />
                            <p>{{ $t('admin.doubts.noMessages') }}</p>
                        </div>
                    </div>

                    <div v-if="doubt.status !== 3" class="weeduc-doubt-composer">
                        <v-textarea
                            v-model="newReply"
                            :placeholder="$t('admin.doubts.replyPlaceholder')"
                            variant="outlined"
                            density="compact"
                            class="weeduc-admin-v-field"
                            hide-details
                            rows="3"
                            auto-grow
                            :disabled="sending"
                            @keydown.ctrl.enter="sendReply"
                        />
                        <v-btn
                            color="primary"
                            variant="flat"
                            rounded="lg"
                            class="text-none weeduc-doubt-composer__send"
                            :loading="sending"
                            :disabled="sending || !newReply.trim()"
                            @click="sendReply"
                        >
                            <fa icon="fas fa-paper-plane" class="me-1" />
                            {{ $t('common.send') }}
                        </v-btn>
                    </div>
                    <div v-else class="weeduc-doubt-closed">
                        <fa icon="fas fa-lock" />
                        <p>{{ $t('admin.doubts.deletedNotice') }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import ApiInquiryMessage from '@/utils/mensage';
import { toast } from 'vue3-toastify';
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';

export default {
    name: 'AdminDoubt',
    components: { TitlePage, Loader },
    data() {
        return {
            doubtId: this.$route.params.slug,
            messages: [],
            newReply: '',
            loading: true,
            sending: false,
            isActive: true,
            doubt: {
                idInquiry: '',
                idMessage: null,
                idUser: '',
                title: '',
                description: null,
                status: 0,
                dateInsert: '',
                userName: '',
            },
        };
    },
    computed: {
        ...mapState(['token']),
        headerSubtitle() {
            if (this.loading) return this.$t('admin.doubts.loadingConversation');
            const name = this.doubt.userName || '—';
            return this.$t('admin.doubts.openedBy', { name });
        },
    },
    methods: {
        getStatusText(status) {
            switch (status) {
                case 0:
                    return this.$t('admin.doubts.unanswered');
                case 1:
                    return this.$t('admin.doubts.answered');
                case 3:
                    return this.$t('admin.doubts.statusDeleted');
                default:
                    return this.$t('admin.doubts.statusUnknown');
            }
        },
        async getMessages() {
            this.loading = true;
            const id = this.$route.params.slug;
            try {
                const response = await ApiInquiryMessage.getDataMensageByInquiryId(
                    this.token,
                    id
                );
                this.messages = response.messages;
                this.doubt = response.inquiry;
                this.$nextTick(() => this.scrollToBottom());
            } catch (error) {
                toast.error(this.$t('admin.doubts.loadError'));
                console.error('Erro ao carregar mensagens:', error);
            } finally {
                this.loading = false;
            }
        },
        async sendReply() {
            if (!this.newReply.trim() || this.sending) return;

            this.sending = true;
            try {
                const payload = {
                    message: this.newReply,
                };

                const response = await ApiInquiryMessage.createNewMessage(
                    payload,
                    this.doubtId,
                    this.token
                );

                if (response) {
                    this.messages.push({
                        message: this.newReply,
                        dateInsert: new Date(),
                        idUser: this.doubt.idTeacher,
                        userName: this.$t('admin.doubts.teacherLabel'),
                        userFinalPhoto: null,
                    });

                    this.newReply = '';
                    this.scrollToBottom();
                }
            } catch (error) {
                toast.error(this.$t('admin.doubts.sendError'));
                console.error('Erro ao enviar resposta:', error);
            } finally {
                this.sending = false;
            }
        },
        async closeDoubt() {
            if (!confirm(this.$t('admin.doubts.closeConfirm'))) return;

            this.loading = true;
            try {
                this.isActive = false;
            } catch (error) {
                console.error('Erro ao fechar dúvida:', error);
            } finally {
                this.loading = false;
            }
        },
        formatDate(date) {
            return new Date(date).toLocaleString();
        },
        scrollToBottom() {
            setTimeout(() => {
                const container = this.$refs.chatMessages;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            }, 100);
        },
    },
    created() {
        this.getMessages();
    },
};
</script>

<style scoped>
@import url('@/css/weeduc/admin/dashboard-weflix-shell.css');

.weeduc-doubt-page :deep(.weeduc-admin-page__shell) {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.weeduc-doubt-panel {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding: 0;
    overflow: hidden;
}

.weeduc-doubt-loading {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    padding: 2rem;
}

.weeduc-doubt-status {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.4;
}

.weeduc-doubt-status.is-answered {
    background: #e8f5e9;
    color: #1f7a57;
}

.weeduc-doubt-status.is-pending {
    background: #f0f2f5;
    color: #5c6370;
}

.weeduc-doubt-status.is-deleted {
    background: #fdecea;
    color: #c62828;
}

.weeduc-doubt-date {
    font-size: 0.8125rem;
    color: #5c6370;
}

.weeduc-doubt-chat {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    height: 100%;
}

.weeduc-doubt-messages {
    flex: 1;
    min-height: 280px;
    max-height: calc(100vh - 340px);
    overflow-y: auto;
    padding: 1.25rem 1.35rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 transparent;
}

.weeduc-doubt-messages::-webkit-scrollbar {
    width: 6px;
}

.weeduc-doubt-messages::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
}

.weeduc-doubt-message {
    max-width: min(720px, 88%);
    animation: weeducDoubtFadeIn 0.25s ease-out;
}

.weeduc-doubt-message.is-student {
    align-self: flex-start;
}

.weeduc-doubt-message.is-teacher {
    align-self: flex-end;
}

.weeduc-doubt-message__header {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    margin-bottom: 0.45rem;
}

.weeduc-doubt-message.is-teacher .weeduc-doubt-message__header {
    align-items: flex-end;
    text-align: right;
}

.weeduc-doubt-message.is-student .weeduc-doubt-message__header {
    align-items: flex-start;
    text-align: left;
}

.weeduc-doubt-message__user {
    display: flex;
    align-items: center;
    gap: 0.65rem;
}

.weeduc-doubt-message.is-teacher .weeduc-doubt-message__user {
    flex-direction: row-reverse;
}

.weeduc-doubt-message__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #e8eaf0;
    background: #eef0fc;
}

.weeduc-doubt-message__name {
    font-weight: 600;
    font-size: 0.9375rem;
    color: #1a1d26;
}

.weeduc-doubt-message__time {
    font-size: 0.75rem;
    color: #5c6370;
}

.weeduc-doubt-message__body {
    padding: 0.9rem 1.1rem;
    border-radius: 12px;
    line-height: 1.55;
    font-size: 0.9375rem;
    color: #1a1d26;
    border: 1px solid #e8eaf0;
    word-break: break-word;
}

.weeduc-doubt-message.is-student .weeduc-doubt-message__body {
    background: #f4f5f8;
    text-align: left;
}

.weeduc-doubt-message.is-teacher .weeduc-doubt-message__body {
    background: rgba(31, 30, 97, 0.06);
    border-color: rgba(31, 30, 97, 0.12);
    text-align: right;
}

.weeduc-doubt-composer {
    display: flex;
    align-items: flex-end;
    gap: 0.85rem;
    padding: 1rem 1.35rem 1.15rem;
    border-top: 1px solid #e8eaf0;
    background: linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
}

.weeduc-doubt-composer :deep(.v-textarea) {
    flex: 1;
}

.weeduc-doubt-composer__send {
    flex-shrink: 0;
    min-height: 42px;
}

.weeduc-doubt-composer__send :deep(svg) {
    color: inherit;
}

.weeduc-doubt-empty,
.weeduc-doubt-closed {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 3.5rem 1.5rem;
    color: #5c6370;
    text-align: center;
    flex: 1;
}

.weeduc-doubt-empty :deep(svg),
.weeduc-doubt-closed :deep(svg) {
    font-size: 2.25rem;
    opacity: 0.55;
}

.weeduc-doubt-closed {
    border-top: 1px solid #e8eaf0;
    padding: 1.25rem 1.35rem;
    flex: 0 0 auto;
}

@keyframes weeducDoubtFadeIn {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 768px) {
    .weeduc-doubt-message {
        max-width: 94%;
    }

    .weeduc-doubt-messages {
        max-height: calc(100vh - 380px);
        padding: 1rem;
    }

    .weeduc-doubt-composer {
        flex-direction: column;
        align-items: stretch;
    }

    .weeduc-doubt-composer__send {
        width: 100%;
    }

    .weeduc-admin-page__header-actions {
        width: 100%;
        justify-content: flex-start;
    }
}
</style>
